import { el, clear, debounce } from '../../utils/dom.js';
import { Rentals } from '../../api/resources.js';
import { dataTable } from '../../components/table.js';
import { fmtDateTime, fmtMoney } from '../../utils/format.js';

// Etapas de una renta (las mismas de la ficha).
export const ETAPAS = {
  cotizacion: ['Cotización', 'muted'],
  pendiente: ['Por confirmar', 'warn'],
  confirmada: ['Confirmada', 'info'],
  en_curso: ['Entregada', 'primary'],
  atrasada: ['Atrasada', 'danger'],
  devuelta: ['Devuelta', 'success'],
  cerrada: ['Cerrada', 'muted'],
  cancelada: ['Cancelada', 'muted'],
};

export function etapaBadge(r) {
  const k = r.etapa === 'en_curso' && new Date(r.end_date) < new Date() ? 'atrasada' : r.etapa;
  const [label, cls] = ETAPAS[k] || [k, 'muted'];
  return el('span', { class: `badge ${cls}` }, label);
}

// Activas = lo que todavía se mueve; pasadas = lo que ya terminó (con su registro).
const VISTAS = {
  activas: { nombre: 'Activas', etapas: ['cotizacion', 'pendiente', 'confirmada', 'en_curso'], vacio: 'No hay rentas activas.' },
  pasadas: { nombre: 'Pasadas', etapas: ['devuelta', 'cerrada', 'cancelada'], vacio: 'Todavía no hay rentas terminadas.' },
  todas: { nombre: 'Todas', etapas: Object.keys(ETAPAS).filter((k) => k !== 'atrasada'), vacio: 'Sin rentas.' },
};
const vistaDe = (etapa) => (VISTAS.pasadas.etapas.includes(etapa) ? 'pasadas' : etapa ? 'activas' : 'activas');

// Fecha corta para la tabla: "24 oct, 1:23 p. m." (el año solo si no es este).
const corta = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const mismoAnio = d.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat('es-DO', { day: 'numeric', month: 'short', ...(mismoAnio ? {} : { year: 'numeric' }), hour: 'numeric', minute: '2-digit', timeZone: 'America/Santo_Domingo' }).format(d);
};

const hace = (iso) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  if (min < 1440) return `hace ${Math.round(min / 60)} h`;
  const d = Math.round(min / 1440);
  return d < 31 ? `hace ${d} día${d === 1 ? '' : 's'}` : fmtDateTime(iso).split(',')[0];
};

export default async function RentalsListPage(outlet, ctx) {
  clear(outlet);
  const q0 = ctx?.query || {};
  const state = { page: 1, q: q0.q || '', etapa: q0.etapa || '', vista: VISTAS[q0.vista] ? q0.vista : vistaDe(q0.etapa), desde: q0.desde || '', hasta: q0.hasta || '' };

  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {}, el('h1', {}, 'Rentas'), el('div', { class: 'muted' }, 'Las de ahora y las pasadas, cada una con su registro')),
    el('div', { class: 'flex gap-2 wrap' },
      el('a', { href: '#/calendario', class: 'btn' }, 'Calendario'),
      el('a', { href: '#/rentals/credito', class: 'btn' }, 'Crédito'),
      el('a', { href: '#/rentals/espera', class: 'btn' }, 'Lista de espera'),
      el('a', { href: '#/rentals/reporte', class: 'btn' }, 'Reporte'),
      el('a', { href: '#/etiquetas', class: 'btn' }, 'Etiquetas QR'),
      el('a', { href: '#/rentals/mensajes', class: 'btn' }, 'WhatsApp'),
      el('a', { href: '#/rentals/ajustes', class: 'btn' }, 'Ajustes'),
      el('a', { href: '#/rentals/new', class: 'btn primary' }, '+ Nueva renta'),
    ),
  ));

  // Pestañas Activas / Pasadas / Todas.
  const tabs = el('div', { class: 'rl-tabs', role: 'tablist' });
  const pintarTabs = () => tabs.replaceChildren(...Object.entries(VISTAS).map(([k, v]) =>
    el('button', { class: `rl-tab ${state.vista === k ? 'on' : ''}`, role: 'tab', 'aria-selected': String(state.vista === k),
      onclick: () => { state.vista = k; state.etapa = ''; state.page = 1; pintarTabs(); pintarChips(); refresh(); } }, v.nombre)));

  const search = el('input', { class: 'input grow', placeholder: 'Buscar por código, cliente o teléfono…', value: state.q, 'aria-label': 'Buscar',
    oninput: debounce((e) => { state.q = e.target.value; state.page = 1; refresh(); }, 300) });
  const desde = el('input', { class: 'input', type: 'date', value: state.desde, 'aria-label': 'Entrega desde', title: 'Entrega desde',
    onchange: (e) => { state.desde = e.target.value; state.page = 1; refresh(); } });
  const hasta = el('input', { class: 'input', type: 'date', value: state.hasta, 'aria-label': 'Entrega hasta', title: 'Entrega hasta',
    onchange: (e) => { state.hasta = e.target.value; state.page = 1; refresh(); } });

  const chips = el('div', { class: 'flex gap-2 wrap' });
  const pintarChips = () => chips.replaceChildren(
    ...[['', 'Todas'], ...VISTAS[state.vista].etapas.map((k) => [k, ETAPAS[k][0]])].map(([k, l]) =>
      el('button', { class: `btn sm ${state.etapa === k ? 'primary' : ''}`, onclick: () => { state.etapa = k; state.page = 1; pintarChips(); refresh(); } }, l)));
  pintarTabs();
  pintarChips();
  outlet.appendChild(tabs);
  outlet.appendChild(el('div', { class: 'toolbar wrap' }, search,
    el('span', { class: 'muted text-sm' }, 'Entrega entre'), desde, hasta));
  outlet.appendChild(el('div', { class: 'mb-3' }, chips));

  const resumen = el('div', { class: 'rl-resumen' });
  outlet.appendChild(resumen);
  const host = el('div');
  outlet.appendChild(host);

  async function refresh() {
    host.innerHTML = '<div class="empty">Cargando...</div>';
    try {
      const res = await Rentals.list({
        page: state.page, limit: 25, q: state.q || undefined, etapa: state.etapa || undefined,
        vista: state.vista === 'todas' ? undefined : state.vista,
        from_date: state.desde ? new Date(`${state.desde}T00:00:00-04:00`).toISOString() : undefined,
        to_date: state.hasta ? new Date(`${state.hasta}T23:59:59-04:00`).toISOString() : undefined,
      });
      const s = res.resumen;
      resumen.replaceChildren(...(s ? [
        el('div', {}, el('span', { class: 'muted' }, 'Rentas'), el('strong', {}, String(s.rentas))),
        el('div', {}, el('span', { class: 'muted' }, 'Total'), el('strong', {}, fmtMoney(s.total))),
        el('div', {}, el('span', { class: 'muted' }, 'Cobrado'), el('strong', {}, fmtMoney(s.cobrado))),
        el('div', {}, el('span', { class: 'muted' }, 'Por cobrar'), el('strong', { class: s.por_cobrar > 0.01 ? 'rl-debe' : '' }, fmtMoney(s.por_cobrar))),
      ] : []));
      host.replaceChildren(dataTable({
        columns: [
          { key: 'code',        label: 'Código', render: (r) => el('span', {},
            el('a', { href: `#/rentals/${r.id}`, class: 'text-mono text-bold rl-code' }, r.code),
            r.origen === 'web' && !r.aprobada_at && r.etapa === 'pendiente' ? el('span', { class: 'badge info', style: 'margin-left:6px' }, 'Web por aprobar') : null) },
          { key: 'client_name', label: 'Cliente', render: (r) => el('div', {}, r.client_name || '—',
            r.client_phone ? el('div', { class: 'muted text-sm' }, r.client_phone) : null) },
          { key: 'equipos',     label: 'Equipos', render: (r) => {
            const l = r.equipos || [];
            return el('span', { class: 'text-sm', title: l.join(', ') }, l.length ? `${l.slice(0, 2).join(', ')}${l.length > 2 ? ` +${l.length - 2}` : ''}` : '—');
          } },
          { key: 'etapa',       label: 'Estado', render: etapaBadge },
          { key: 'start_date',  label: 'Entrega', render: (r) => el('span', { class: 'rl-fecha' }, corta(r.start_date)) },
          { key: 'end_date',    label: 'Devolución', render: (r) => el('span', { class: 'rl-fecha' }, corta(r.actual_return_date || r.end_date)) },
          { key: 'total',       label: 'Total', className: 'right', render: (r) => fmtMoney(r.total) },
          { key: 'balance',     label: 'Saldo', className: 'right', render: (r) => {
            if (r.etapa === 'cancelada' || r.etapa === 'cotizacion') return el('span', { class: 'muted' }, '—');
            const saldo = Math.max(0, Number(r.total || 0) - Number(r.paid_amount || 0));
            return el('span', { class: saldo > 0.01 ? 'badge warn' : 'badge muted' }, fmtMoney(saldo));
          } },
          { key: 'ultimo',      label: 'Registro', render: (r) => el('a', { href: `#/rentals/${r.id}`, class: 'rl-reg' },
            r.ultimo ? el('span', {}, r.ultimo.texto, el('span', { class: 'muted text-sm' }, ` · ${hace(r.ultimo.cuando)}${r.ultimo.quien ? ` · ${r.ultimo.quien}` : ''}`)) : 'Ver',
            r.movimientos > 1 ? el('span', { class: 'muted text-sm' }, ` (${r.movimientos} movimientos)`) : null) },
        ],
        rows: res.data || [],
        empty: state.q || state.etapa || state.desde || state.hasta ? 'Nada con esos filtros.' : VISTAS[state.vista].vacio,
        pagination: res.pagination ? { page: res.pagination.page, pages: res.pagination.pages, onPage: (p) => { state.page = p; refresh(); } } : null,
      }));
    } catch (ex) {
      host.innerHTML = `<div class="empty">Error: ${ex.message}</div>`;
    }
  }
  refresh();
}
